if (!customElements.get('gallery-story')) {
  const selections = new Map();
  customElements.define('gallery-story', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.events?.abort();
      this.dialog?.close();
    }
    initialize() {
      this.events?.abort();
      this.events = new AbortController();
      const on = (element, type, handler, options = {}) => element.addEventListener(type, handler, {...options, signal: this.events.signal});
      const tabs = [...this.querySelectorAll('[data-orbit-tab]')];
      const panels = [...this.querySelectorAll('[data-orbit-panel]')];
      if (!panels.length) return;
      const tablist = this.querySelector('.og-tabs');
      const controls = this.querySelector('.og-controls');
      const library = this.querySelector('.og-library');
      const browse = this.querySelector('[data-orbit-browse]');
      const jumps = [...this.querySelectorAll('[data-orbit-jump]')];
      const explorer = this.querySelector('.og-explorer');
      const desktop = matchMedia('(min-width: 900px)');
      const revealGallery = () => (controls.hidden ? this.querySelector('.og-explorer') : controls).scrollIntoView({block: 'start', behavior: 'instant'});
      const angles = tabs.map(() => 0);
      let active = 0;
      const orbit = index => {
        this.classList.toggle('is-browsing-tabs', index !== active);
        tabs.forEach((tab, i) => {
          let offset = ((i - index) % tabs.length + tabs.length) % tabs.length;
          if (offset > tabs.length / 2) offset -= tabs.length;
          if (!desktop.matches && tabs.length === 2) offset = i - index;
          const angle = offset * (desktop.matches ? 360 / Math.min(tabs.length, 5) : 52);
          const satellite = tab.parentElement;
          // Recycled thumbnails enter at the arc's edge, never sweep across its centre.
          satellite.classList.toggle('is-repositioning', !desktop.matches && Math.abs(angle - angles[i]) > 78);
          angles[i] = desktop.matches ? angles[i] + ((angle - angles[i]) % 360 + 540) % 360 - 180 : angle;
          satellite.classList.toggle('is-outside', Math.abs(offset) > (desktop.matches ? 2 : 1.5));
          satellite.style.setProperty('--tab-angle', `${angles[i]}deg`);
        });
      };
      const select = (index, remember = true) => {
        if (remember) this.classList.add('has-motion');
        active = ((index % panels.length) + panels.length) % panels.length;
        orbit(active);
        panels.forEach((panel, i) => { panel.hidden = i !== active; });
        tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === active)); tab.tabIndex = i === active ? 0 : -1; });
        jumps.forEach((jump, i) => { jump.setAttribute('aria-current', String(i === active)); });
        this.querySelector('[data-orbit-count]').textContent = `${active + 1} / ${panels.length}`;
        const image = panels[active].querySelector('.og-photo img');
        if (image) image.loading = 'eager';
        if (remember) {
          selections.set(this.dataset.sectionId, panels[active].dataset.slot);
        }
      };
      tablist.setAttribute('role', 'tablist');
      tabs.forEach((tab, i) => {
        tab.setAttribute('role', 'tab');
        panels[i].setAttribute('role', 'tabpanel');
        panels[i].setAttribute('aria-labelledby', tab.id);
        panels[i].tabIndex = 0;
        on(tab, 'click', () => select(i));
        on(tab, 'keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = (i + 1) % tabs.length;
          if (event.key === 'ArrowLeft') target = (i - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = tabs.length - 1;
          if (target === undefined) return; // Enter/Space use the native button click.
          event.preventDefault();
          const reveal = tabs[target].parentElement.classList.contains('is-outside');
          if (reveal) tabs[target].parentElement.classList.remove('is-outside');
          tabs.forEach((item, j) => { item.tabIndex = j === target ? 0 : -1; });
          tabs[target].focus({preventScroll: true});
          if (reveal) orbit(target);
        });
      });
      // Keyboard selection is manual, so focusing a new tab never waits on a photo download.
      on(tablist, 'focusout', event => {
        if (!tablist.contains(event.relatedTarget)) {
          tabs.forEach((tab, i) => { tab.tabIndex = i === active ? 0 : -1; });
          orbit(active);
        }
      });
      on(this.querySelector('[data-orbit-prev]'), 'click', () => select(active - 1));
      on(this.querySelector('[data-orbit-next]'), 'click', () => select(active + 1));
      // The mobile arc follows horizontal movement; pan-y and pinch-zoom stay native.
      let drag;
      let suppressClick = false;
      const finishDrag = commit => {
        const ended = drag;
        drag = null;
        this.classList.remove('is-dragging');
        if (!ended) return;
        if (explorer.hasPointerCapture(ended.id)) explorer.releasePointerCapture(ended.id);
        if (ended.started) {
          suppressClick = true;
          select(commit ? Math.round(ended.position) : active);
        }
      };
      on(window, 'pointerdown', event => {
        // A second finger can begin outside the gallery during a pinch.
        if (drag && event.pointerId !== drag.id) { finishDrag(false); return; }
        if (desktop.matches || panels.length < 2 || !event.isPrimary || event.button !== 0) return;
        suppressClick = false;
        if (!explorer.contains(event.target) || !event.target.closest('.og-tabs, .og-photo')) return;
        drag = {id: event.pointerId, x: event.clientX, y: event.clientY, index: active, position: active, step: tablist.clientWidth * .33, started: false};
      }, {capture: true});
      on(explorer, 'pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!drag.started) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
          if (Math.abs(dy) >= Math.abs(dx)) { finishDrag(false); return; }
          drag.started = true;
          explorer.setPointerCapture(event.pointerId);
          this.classList.add('is-dragging');
        }
        drag.position = drag.index - dx / drag.step;
        if (panels.length === 2) drag.position = Math.max(0, Math.min(1, drag.position));
        orbit(drag.position);
      });
      on(window, 'pointerup', event => { if (event.pointerId === drag?.id) finishDrag(true); });
      on(window, 'pointercancel', event => { if (event.pointerId === drag?.id) finishDrag(false); });
      on(explorer, 'lostpointercapture', event => { if (event.target === explorer) finishDrag(false); });
      on(explorer, 'click', event => {
        if (!suppressClick || event.detail === 0) return;
        suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, {capture: true});
      on(explorer, 'dragstart', event => { if (!desktop.matches && event.target.closest('.og-tabs, .og-photo')) event.preventDefault(); });
      const remembered = panels.findIndex(panel => panel.dataset.slot === selections.get(this.dataset.sectionId));
      select(remembered < 0 ? 0 : remembered, false);
      this.classList.add('is-ready');
      this.classList.toggle('is-single', panels.length === 1);
      tablist.hidden = panels.length < 2;
      this.querySelector('.og-orbits').hidden = panels.length < 2;
      controls.hidden = panels.length < 2;
      library.hidden = panels.length <= 5;
      browse.hidden = library.hidden;
      this.querySelector('[data-orbit-total]').textContent = `(${panels.length})`;
      on(library, 'toggle', () => browse.setAttribute('aria-expanded', String(library.open)));
      on(browse, 'click', () => {
        library.open = !library.open;
        if (library.open) {
          library.querySelector('summary').focus({preventScroll: true});
          library.scrollIntoView({block: 'start', behavior: 'instant'});
        }
      });
      jumps.forEach((jump, i) => on(jump, 'click', () => {
        select(i);
        library.open = false;
        panels[active].focus({preventScroll: true});
        revealGallery();
      }));
      on(this, 'shopify:block:select', event => {
        const index = panels.findIndex(panel => panel.dataset.slot === event.detail.blockId);
        if (index < 0) return;
        finishDrag(false);
        this.dialog?.close();
        select(index);
        this.classList.remove('has-motion');
        revealGallery();
      });
      // Re-enable motion only on the next selection, never during a layout change.
      on(desktop, 'change', () => { finishDrag(false); this.classList.remove('has-motion'); orbit(active); });

      const dialog = this.dialog = this.querySelector('dialog');
      const photos = [...this.querySelectorAll('[data-gallery-photo]')];
      if (!dialog.showModal || !photos.length) return;
      let currentPhoto = 0;
      let opener;
      dialog.querySelector('.og-lightbox__arrows').hidden = photos.length < 2;
      const showPhoto = index => {
        currentPhoto = (index + photos.length) % photos.length;
        const photo = photos[currentPhoto];
        const image = dialog.querySelector('[data-gallery-image]');
        image.src = photo.href;
        image.alt = photo.dataset.alt;
        dialog.querySelector('[data-gallery-caption]').textContent = photo.dataset.caption;
        dialog.querySelector('[data-gallery-count]').textContent = `${currentPhoto + 1} / ${photos.length}`;
        select(panels.indexOf(photo.closest('[data-orbit-panel]')));
      };
      photos.forEach((photo, i) => {
        const open = event => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          opener = event.currentTarget;
          showPhoto(i);
          dialog.showModal();
        };
        on(photo, 'click', open);
        on(photo.closest('[data-orbit-panel]').querySelector('[data-orbit-enlarge]'), 'click', open);
      });
      on(dialog.querySelector('[data-gallery-close]'), 'click', () => dialog.close());
      on(dialog.querySelector('[data-gallery-prev]'), 'click', () => showPhoto(currentPhoto - 1));
      on(dialog.querySelector('[data-gallery-next]'), 'click', () => showPhoto(currentPhoto + 1));
      on(dialog, 'keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          showPhoto(currentPhoto + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      on(dialog, 'click', event => {
        const box = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) dialog.close();
      });
      on(dialog, 'close', () => {
        const target = panels[active].contains(opener) ? opener : panels[active].querySelector('[data-gallery-photo]');
        target?.focus();
      });
    }
  });
}
